import { getUrl } from "aws-amplify/storage";
import type {
  MerchantSignalRow,
  SignalsAggregates,
  SignalsSnapshot,
} from "./types";

const PREFIX = "signals/";
const POINTER_KEY = `${PREFIX}latest.json`;
const URL_TTL_SECONDS = 300;

interface LatestPointer {
  buildId: string;
  generatedAt: string;
  aggregatesKey: string;
  profilesKey: string;
}

export interface SignalsRepository {
  loadBuild(): Promise<SignalsSnapshot>;
}

/** A build artifact could not be read. `status` is the S3 response status. */
export class ArtifactReadError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
  ) {
    super(`Could not read ${path} (HTTP ${status})`);
    this.name = "ArtifactReadError";
  }
}

/**
 * Reads the newest build's artifacts.
 *
 * Both files are resolved through a single `latest.json` pointer the build
 * writes last, so a build that failed partway can never be read as a mix of two
 * builds, and no listing is needed to find the newest one.
 */
export class StorageSignalsRepository implements SignalsRepository {
  async loadBuild(): Promise<SignalsSnapshot> {
    const pointer = await this.read<LatestPointer>(POINTER_KEY);
    const [aggregates, rows] = await Promise.all([
      this.read<SignalsAggregates>(`${PREFIX}${pointer.aggregatesKey}`),
      this.read<{ rows: MerchantSignalRow[] }>(
        `${PREFIX}${pointer.profilesKey}`,
      ).then((artifact) => artifact.rows),
    ]);
    return { aggregates, rows };
  }

  /**
   * Artifacts are read through a signed URL rather than `downloadData` because
   * they are stored with `Content-Encoding: gzip`. `downloadData` receives the
   * body as a Blob, and a Blob built from a gzip response whose inflated size
   * far exceeds its `Content-Length` comes back empty once it is large enough to
   * leave the browser's in-memory buffer — silently yielding `""` to parse.
   * `fetch` inflates the same response correctly at any size.
   */
  private async read<T>(path: string): Promise<T> {
    const { url } = await getUrl({
      path,
      options: { expiresIn: URL_TTL_SECONDS },
    });
    const response = await fetch(url);
    if (!response.ok) throw new ArtifactReadError(path, response.status);
    return (await response.json()) as T;
  }
}

/**
 * Thrown when no build has run yet, so the page can say so rather than
 * rendering an error.
 */
export function isMissingArtifact(error: unknown): boolean {
  if (error instanceof ArtifactReadError) return error.status === 404;
  const name = (error as { name?: string } | null)?.name ?? "";
  const message = error instanceof Error ? error.message : String(error);
  return name === "NoSuchKey" || message.includes("NoSuchKey");
}
